// START COMMANDBAR
import css2xpath from 'css2xpath';
// END COMMANDBAR

/*
 * SVG elements are in a different namespace and must be queried with local-name()
 * https://developer.mozilla.org/en-US/docs/Web/SVG/Namespaces_Crash_Course
 *
 * To make xpath with svg elements work in both namespaces,
 * we replace tags in the svg namespace with *[local-name()="tag-name"]
 */
export const xpathWithSVGFix = (xpath: string) =>
  xpath.replace(/(\/|\/\/)(svg|g|path|circle|rect|line|ellipse|polygon|polyline|text)/g, '$1*[local-name()="$2"]');

const pathToXpath = (path: Path) => xpathWithSVGFix(css2xpath(selector(path)).substring(1));

type Node = {
  // START COMMANDBAR
  type: 'id' | 'text' | 'attr' | 'class' | 'tag' | 'other';
  // END COMMANDBAR
  name: string;
  penalty: number;
  level?: number;
};

type Path = Node[];

enum Limit {
  All,
  Two,
  One,
}

export type Options = {
  root: Element;
  idName: (name: string) => boolean;
  className: (name: string) => boolean;
  tagName: (name: string) => boolean;
  attr: (name: string, value: string) => boolean;
  seedMinLength: number;
  optimizedMinLength: number;
  threshold: number;
  maxNumberOfTries: number;
};

let config: Options;

export function finder(input: Element, options?: Partial<Options>) {
  if (input.nodeType !== Node.ELEMENT_NODE) {
    throw new Error(`Can't generate CSS selector for non-element node type.`);
  }

  if ('html' === input.tagName.toLowerCase()) {
    return 'html';
  }

  const defaults: Options = {
    root: document.body,
    idName: (_name: string) => true,
    className: (_name: string) => true,
    tagName: (_name: string) => true,
    // START COMMANDBAR
    // these attributes are typically stable and descriptive of an element
    attr: (name: string, _value: string) => ['aria-label', 'name', 'role'].includes(name),
    // END COMMANDBAR
    seedMinLength: 1,
    optimizedMinLength: 2,
    threshold: 1000,
    maxNumberOfTries: 10000,
  };

  config = { ...defaults, ...options };

  const path = bottomUpSearch(input, Limit.All, () =>
    bottomUpSearch(input, Limit.Two, () => bottomUpSearch(input, Limit.One)),
  );

  if (path) {
    const optimized = sort(optimize(path, input));

    // START COMMANDBAR
    // convert css + xpath hybrid selector to vanilla xpath
    if (optimized.length > 0) {
      return pathToXpath(optimized[0]);
    }

    return pathToXpath(path);
    // END COMMANDBAR
  } else {
    throw new Error(`Selector was not found.`);
  }
}

function bottomUpSearch(input: Element, limit: Limit, fallback?: () => Path | null): Path | null {
  let path: Path | null = null;
  const stack: Node[][] = [];
  let current: Element | null = input;
  let i = 0;
  // START COMMANDBAR
  let level: Node[];
  // END COMMANDBAR

  while (current && current !== config.root.parentElement) {
    // START COMMANDBAR
    const matchTypes = stack.flat().map(({ type }) => type);

    /*
     * INFO: We want to use text to specify an element once. If we allow multiple text matches,
     * then we can end up with selectors depending on nested text selectors which would be
     * brittle.
     * If an element is matched by text but we still require more specificty, then elaborate using
     * other means if possible.
     */
    if (matchTypes.includes('text')) {
      level = maybe(id(current)) ||
        maybe(...attr(current)) ||
        maybe(...classNames(current)) ||
        maybe(text(current)) || // text as a subsequent match is preferred only if attr and classes are exhausted
        maybe(tagName(current)) || [any()];
      //  Don't match contenteditable elements by text
    } else if (current.attributes.getNamedItem('contenteditable')) {
      level = maybe(id(current)) ||
        maybe(...attr(current)) ||
        maybe(...classNames(current)) ||
        maybe(tagName(current)) || [any()];
    } else {
      level = maybe(id(current)) ||
        maybe(text(current)) ||
        maybe(...attr(current)) ||
        maybe(...classNames(current)) ||
        maybe(tagName(current)) || [any()];
    }
    // END COMMANDBAR

    const nth = index(current);

    if (limit === Limit.All) {
      if (nth) {
        level = level.concat(level.filter(dispensableNth).map((node) => nthChild(node, nth)));
      }
    } else if (limit === Limit.Two) {
      level = level.slice(0, 1);

      if (nth) {
        level = level.concat(level.filter(dispensableNth).map((node) => nthChild(node, nth)));
      }
    } else if (limit === Limit.One) {
      const [node] = (level = level.slice(0, 1));

      if (nth && dispensableNth(node)) {
        level = [nthChild(node, nth)];
      }
    }

    for (const node of level) {
      node.level = i;
    }

    stack.push(level);

    if (stack.length >= config.seedMinLength) {
      path = findUniquePath(stack, fallback);
      if (path) {
        break;
      }
    }

    current = current.parentElement;
    i++;
  }

  if (!path) {
    path = findUniquePath(stack, fallback);
  }

  return path;
}

function findUniquePath(stack: Node[][], fallback?: () => Path | null): Path | null {
  const paths = sort(combinations(stack));

  if (paths.length > config.threshold) {
    return fallback ? fallback() : null;
  }

  for (const candidate of paths) {
    if (unique(candidate)) {
      return candidate;
    }
  }

  return null;
}

function selector(path: Path): string {
  let node = path[0];
  let query = node.name;
  for (let i = 1; i < path.length; i++) {
    const level = path[i].level || 0;

    if (node.level === level - 1) {
      query = `${path[i].name} > ${query}`;
    } else {
      query = `${path[i].name} ${query}`;
    }

    node = path[i];
  }
  return query;
}

function penalty(path: Path): number {
  return path.map((node) => node.penalty).reduce((acc, i) => acc + i, 0);
}

function unique(path: Path) {
  // START COMMANDBAR
  // Determine if we have a unique selector by counting matches in document.body
  const evaluator = new XPathEvaluator();

  const expression = evaluator.createExpression(pathToXpath(path));
  const result = expression.evaluate(document.body, XPathResult.UNORDERED_NODE_SNAPSHOT_TYPE);

  switch (result.snapshotLength) {
    // END COMMANDBAR
    case 0:
      throw new Error(`Can't select any node with this selector: ${selector(path)}`);
    case 1:
      return true;
    default:
      return false;
  }
}

function id(input: Element): Node | null {
  const elementId = input.getAttribute('id');
  if (elementId && config.idName(elementId)) {
    return {
      // START COMMANDBAR
      type: 'id',
      // END COMMANDBAR
      name: '#' + cssesc(elementId, { isIdentifier: true }),
      penalty: 0,
    };
  }
  return null;
}

// START COMMANDBAR
const text = (input: Element): Node | null => {
  const commandbarWrapper = document.getElementById('commandbar-wrapper');
  const proxyWrapper = document.getElementById('proxy-wrapper');
  const editor = document.getElementById('commandbar-editor-drawer');

  if ([commandbarWrapper?.parentNode, proxyWrapper?.parentNode, editor?.parentNode].includes(input)) {
    return null;
  }

  const textContent = input.textContent?.trim().replace(/\n\s+/g, ' ');
  const tagName = input.tagName.toLowerCase();

  if (textContent) {
    return {
      type: 'text',
      // css2xpath can parse experimental css selectors like :contains()
      name: `${tagName}:contains("${textContent}")`,
      penalty: 0.25,
    };
  }
  return null;
};
// END COMMANDBAR

function attr(input: Element): Node[] {
  const attrs = Array.from(input.attributes).filter((attr) => config.attr(attr.name, attr.value));

  // START COMMANDBAR
  return attrs.map(
    (attribute): Node => ({
      type: 'attr',
      name: '[' + cssesc(attribute.name, { isIdentifier: true }) + '="' + cssesc(attribute.value) + '"]',
      penalty: cssesc(attribute.name, { isIdentifier: true }) === 'role' ? 0.75 : 0.5,
    }),
  );
  // END COMMANDBAR
}

const classNames = (input: Element): Node[] => {
  const names = Array.from(input.classList).filter(config.className);

  // START COMMANDBAR
  return names.map((name): Node => {
    const className = cssesc(name, { isIdentifier: true });

    return {
      type: 'class',
      name: '.' + className,
      // ? Penalize classes that appear to be auto-generated
      penalty: className.match(regexProbablyAutoGenerated) ? 2.5 : 1,
    };
  });
  // END COMMANDBAR
};

function tagName(input: Element): Node | null {
  const name = input.tagName.toLowerCase();
  if (config.tagName(name)) {
    return {
      // START COMMANDBAR
      type: 'tag',
      // END COMMANDBAR
      name,
      penalty: 2,
    };
  }
  return null;
}

function any(): Node {
  return {
    // START COMMANDBAR
    type: 'other',
    // END COMMANDBAR
    name: '*',
    penalty: 3,
  };
}

function index(input: Element): number | null {
  const parent = input.parentNode;
  if (!parent) {
    return null;
  }

  let child = parent.firstChild;
  if (!child) {
    return null;
  }

  let i = 0;
  while (child) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      i++;
    }

    if (child === input) {
      break;
    }

    child = child.nextSibling;
  }

  return i;
}

function nthChild(node: Node, i: number): Node {
  return {
    // START COMMANDBAR
    type: 'other',
    // END COMMANDBAR
    name: node.name + `:nth-child(${i})`,
    penalty: node.penalty + 1,
  };
}

function dispensableNth(node: Node) {
  return node.name !== 'html' && !node.name.startsWith('#');
}

function maybe(...level: (Node | null)[]): Node[] | null {
  const list = level.filter(notEmpty);
  if (list.length > 0) {
    return list;
  }
  return null;
}

function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function* combinations(stack: Node[][], path: Node[] = []): Generator<Node[]> {
  if (stack.length > 0) {
    for (const node of stack[0]) {
      // @ts-expect-error using down level iteration
      yield* combinations(stack.slice(1, stack.length), path.concat(node));
    }
  } else {
    yield path;
  }
}

function sort(paths: Iterable<Path>): Path[] {
  return Array.from(paths).sort((a, b) => penalty(a) - penalty(b));
}

type Scope = {
  counter: number;
  visited: Map<string, boolean>;
};

function* optimize(
  path: Path,
  input: Element,
  scope: Scope = {
    counter: 0,
    visited: new Map<string, boolean>(),
  },
): Generator<Node[]> {
  if (path.length > 2 && path.length > config.optimizedMinLength) {
    for (let i = 1; i < path.length - 1; i++) {
      if (scope.counter > config.maxNumberOfTries) {
        return; // Okay At least I tried!
      }
      scope.counter += 1;
      const newPath = [...path];
      newPath.splice(i, 1);
      const newPathKey = selector(newPath);
      if (scope.visited.has(newPathKey)) {
        return;
      }
      if (unique(newPath) && same(newPath, input)) {
        yield newPath;
        scope.visited.set(newPathKey, true);
        // @ts-expect-error using down level iteration
        yield* optimize(newPath, input, scope);
      }
    }
  }
}

function same(path: Path, input: Element) {
  // START COMMANDBAR
  const xpath = pathToXpath(path);
  return (
    document.evaluate(xpath, document.body, null, XPathResult.FIRST_ORDERED_NODE_TYPE, null).singleNodeValue === input
  );
  // END COMMANDBAR
}

// eslint-disable-next-line no-useless-escape
const regexAnySingleEscape = /[ -,\.\/:-@\[-\^`\{-~]/;
// eslint-disable-next-line no-useless-escape
const regexSingleEscape = /[ -,\.\/:-@\[\]\^`\{-~]/;
const regexExcessiveSpaces = /(^|\\+)?(\\[A-F0-9]{1,6})\x20(?![a-fA-F0-9\x20])/g;
// START COMMANDBAR
// Basic checking for whether a class name was generated. This would suggest a brittle selector.
const regexProbablyAutoGenerated = /(\d[a-zA-Z]+)|([a-z][A-Z]{2,}[a-z])|(\d[-|_])/;
// END COMMANDBAR

const defaultOptions = {
  escapeEverything: false,
  isIdentifier: false,
  quotes: 'single',
  wrap: false,
};

function cssesc(string: string, opt: Partial<typeof defaultOptions> = {}) {
  const options = { ...defaultOptions, ...opt };
  // eslint-disable-next-line eqeqeq
  if (options.quotes != 'single' && options.quotes != 'double') {
    options.quotes = 'single';
  }
  // eslint-disable-next-line eqeqeq
  const quote = options.quotes == 'double' ? '"' : "'";
  const isIdentifier = options.isIdentifier;

  const firstChar = string.charAt(0);
  let output = '';
  let counter = 0;
  const length = string.length;
  while (counter < length) {
    const character = string.charAt(counter++);
    let codePoint = character.charCodeAt(0);
    let value: string | undefined = void 0;
    // If it’s not a printable ASCII character…
    if (codePoint < 0x20 || codePoint > 0x7e) {
      if (codePoint >= 0xd800 && codePoint <= 0xdbff && counter < length) {
        // It’s a high surrogate, and there is a next character.
        const extra = string.charCodeAt(counter++);
        // eslint-disable-next-line eqeqeq
        if ((extra & 0xfc00) == 0xdc00) {
          // next character is low surrogate
          codePoint = ((codePoint & 0x3ff) << 10) + (extra & 0x3ff) + 0x10000;
        } else {
          // It’s an unmatched surrogate; only append this code unit, in case
          // the next code unit is the high surrogate of a surrogate pair.
          counter--;
        }
      }
      value = '\\' + codePoint.toString(16).toUpperCase() + ' ';
    } else {
      if (options.escapeEverything) {
        if (regexAnySingleEscape.test(character)) {
          value = '\\' + character;
        } else {
          value = '\\' + codePoint.toString(16).toUpperCase() + ' ';
        }
        // eslint-disable-next-line no-control-regex
      } else if (/[\t\n\f\r\x0B]/.test(character)) {
        value = '\\' + codePoint.toString(16).toUpperCase() + ' ';
      } else if (
        // eslint-disable-next-line eqeqeq
        character == '\\' ||
        // eslint-disable-next-line eqeqeq
        (!isIdentifier && ((character == '"' && quote == character) || (character == "'" && quote == character))) ||
        (isIdentifier && regexSingleEscape.test(character))
      ) {
        value = '\\' + character;
      } else {
        value = character;
      }
    }
    output += value;
  }

  if (isIdentifier) {
    if (/^-[-\d]/.test(output)) {
      output = '\\-' + output.slice(1);
    } else if (/\d/.test(firstChar)) {
      output = '\\3' + firstChar + ' ' + output.slice(1);
    }
  }

  // Remove spaces after `\HEX` escapes that are not followed by a hex digit,
  // since they’re redundant. Note that this is only possible if the escape
  // sequence isn’t preceded by an odd number of backslashes.
  output = output.replace(regexExcessiveSpaces, function ($0, $1, $2) {
    if ($1 && $1.length % 2) {
      // It’s not safe to remove the space, so don’t.
      return $0;
    }
    // Strip the space.
    return ($1 || '') + $2;
  });

  if (!isIdentifier && options.wrap) {
    return quote + output + quote;
  }
  return output;
}
